import { SpreadsheetCellRef } from "@common/schemas/spreadsheetReference.schema";
import {
    FlatRow,
    SelectionRange,
    SpreadsheetCell,
    SpreadsheetRowData,
} from "./types";

interface FlattenRowsCtx {
    expandedIds: Set<string>;
    depth: number;
    parentId: string;
}

/** Flatten a tree of SpreadsheetRowData into a list of FlatRow, respecting expanded state. */
export function flattenRows(
    rows: SpreadsheetRowData[],
    ctx: FlattenRowsCtx,
): FlatRow[] {
    const result: FlatRow[] = [];
    for (const row of rows) {
        const id = String(row._id ?? `${ctx.parentId}-${result.length}`);
        const hasChildren = (row.children?.length ?? 0) > 0;
        const flat: FlatRow = {
            ...row,
            _depth: ctx.depth,
            _id: id,
            _hasChildren: hasChildren,
        };
        result.push(flat);
        if (hasChildren && ctx.expandedIds.has(id)) {
            const childRows = flattenRows(row.children!, {
                expandedIds: ctx.expandedIds,
                depth: ctx.depth + 1,
                parentId: id,
            });
            result.push(...childRows);
        }
    }
    return result;
}

/**
 * The ids `flattenRows` gives every row with children, at any depth, so
 * expanding them all shows the whole tree.
 */
export function getExpandableRowIds(rows: SpreadsheetRowData[]): Set<string> {
    const expandedIds = new Set<string>();
    let hasNewIds = true;
    while (hasNewIds) {
        const collapsedParents = flattenRows(rows, {
            expandedIds,
            depth: 0,
            parentId: "",
        }).filter((row) => row._hasChildren && !expandedIds.has(row._id));
        collapsedParents.forEach((row) => expandedIds.add(row._id));
        hasNewIds = collapsedParents.length > 0;
    }
    return expandedIds;
}

interface RangeParts {
    rows: string;
    cols: string;
}

/**
 * Split a list of selected cell refs into individually-styleable row and column
 * label strings. Use `buildRangeLabel` when you need a plain string (e.g. for
 * storing `rangeLabel` on a SpreadsheetReference).
 */
export function buildRangeParts(cells: SpreadsheetCellRef[]): RangeParts {
    if (cells.length === 0) {
        return { rows: "", cols: "" };
    }
    const rowLabels = [...new Set(cells.map((c) => c.rowLabel))];
    const colLabels = [...new Set(cells.map((c) => c.columnLabel))];
    const rows =
        rowLabels.length === 1
            ? rowLabels[0]
            : `${rowLabels[0]} - ${rowLabels[rowLabels.length - 1]}`;
    const cols =
        colLabels.length === 1
            ? colLabels[0]
            : `${colLabels[0]} - ${colLabels[colLabels.length - 1]}`;
    return { rows, cols };
}

/**
 * Collect all (row, col) grid indices covered by a selection range.
 * When `range` has no start/end, only the anchor cell is returned.
 */
export function collectSelectionIndices(
    selectedCell: SpreadsheetCell | null,
    range: SelectionRange,
): Array<{ row: number; col: number }> {
    if (!selectedCell) {
        return [];
    }
    const { start, end } = range;
    if (!start || !end) {
        return [selectedCell];
    }
    const minRow = Math.min(start.row, end.row);
    const maxRow = Math.max(start.row, end.row);
    const minCol = Math.min(start.col, end.col);
    const maxCol = Math.max(start.col, end.col);
    const result: Array<{ row: number; col: number }> = [];
    for (let r = minRow; r <= maxRow; r++) {
        for (let c = minCol; c <= maxCol; c++) {
            result.push({ row: r, col: c });
        }
    }
    return result;
}

/**
 * Build a human-readable range label from a list of selected cell refs.
 * Examples:
 *   "Revenue · Jan 2025"
 *   "Revenue · Jan–Mar 2025"
 *   "Revenue–COGS · Jan–Mar 2025"
 */
export function buildRangeLabel(cells: SpreadsheetCellRef[]): string {
    const { rows, cols } = buildRangeParts(cells);
    return cols ? `${rows} · ${cols}` : rows;
}
